/**
 * Agent Monitor API base client.
 *
 * 원본(teams-tab/src/api/base.ts)과 시그니처 호환을 유지하되,
 * Microsoft Teams SDK 대신 Firebase ID 토큰을 사용하고
 * 모든 요청은 /api/agent-proxy/* 경로로 나간다 (ontology 백엔드가 api-gateway로 forward).
 */

import { auth } from "../../../firebase";

export const API_BASE = "/api/agent-proxy";

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

async function getFirebaseIdToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}

/**
 * teams-tab/src/api/base.ts의 getAuthToken과 같은 시그니처를 유지한다.
 * 기존 호출부(skills.ts 등)가 그대로 동작하도록 Firebase ID 토큰을 반환한다.
 */
export async function getAuthToken(): Promise<string> {
  const token = await getFirebaseIdToken();
  if (!token) throw new ApiError("Not authenticated", 401);
  return token;
}

/**
 * Rewrite a legacy `/api/...` path to the agent-proxy prefix.
 * teams-tab 코드는 `/api/tenants` 같은 절대 경로를 그대로 넘기므로,
 * `/api` prefix를 제거해 `/api/agent-proxy/tenants`로 변환한다.
 */
function toProxyUrl(url: string): string {
  const path = url.startsWith("/api/") ? url.slice(4) : url;
  return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = await getFirebaseIdToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((options?.headers as Record<string, string> | undefined) ?? {}),
  };

  const res = await fetch(toProxyUrl(url), {
    ...options,
    credentials: "include",
    headers,
  });

  if (res.status === 401) {
    throw new ApiError("Authentication failed", 401);
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      body.message || body.error || `API error: ${res.status}`,
      res.status,
      body.code,
    );
  }

  // 일부 엔드포인트는 본문이 없을 수 있음
  if (res.status === 204) return undefined as unknown as T;
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
}

export function mapStatus(s: string): "Running" | "Starting" | "Stopped" {
  if (s === "running") return "Running";
  if (s === "starting") return "Starting";
  return "Stopped";
}
